<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { parseManualMarkup } from './manualMarkup';

const props = defineProps({
  text: { type: String, required: true },
  delayMs: { type: Number, default: 0 },
  reduceMotion: { type: Boolean, default: false },
});

const emit = defineEmits(['spotEnter', 'spotLeave']);
const { t } = useI18n();

const parsed = computed(() => parseManualMarkup(props.text));
const hasSpot = computed(() => Boolean(parsed.value.spot));

const itemStyle = computed(() => {
  if (props.reduceMotion || !props.delayMs) return undefined;
  return { animationDelay: `${props.delayMs}ms` };
});

const onEnter = () => {
  if (parsed.value.spot) emit('spotEnter', parsed.value.spot);
};

const onLeave = () => {
  if (parsed.value.spot) emit('spotLeave', parsed.value.spot);
};
</script>

<template>
  <li
    class="group flex items-start gap-2.5 text-sm leading-relaxed text-n-slate-11"
    :class="
      reduceMotion
        ? 'opacity-100'
        : 'animate-cluster-in motion-reduce:animate-none'
    "
    :style="itemStyle"
    data-testid="page-manual-item"
    :data-has-spot="hasSpot ? 'true' : 'false'"
    :data-stagger-ms="String(delayMs)"
    :tabindex="hasSpot ? 0 : undefined"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @focus="onEnter"
    @blur="onLeave"
  >
    <span
      class="mt-2 size-1.5 shrink-0 rounded-full bg-n-slate-8 group-hover:bg-n-iris-9 group-focus:bg-n-iris-9"
      :class="{ 'bg-n-iris-9': hasSpot }"
      aria-hidden="true"
    />
    <p class="m-0 min-w-0 flex-1">
      <template v-for="(node, index) in parsed.nodes" :key="index">
        <kbd
          v-if="node.type === 'kbd'"
          class="mx-0.5 inline-flex min-h-6 items-center rounded-md border border-n-weak bg-n-solid-2 px-1.5 font-mono text-[11px] font-semibold leading-none text-n-slate-12"
        >
          {{ node.value }}
        </kbd>
        <span
          v-else-if="node.type === 'n'"
          class="mx-0.5 inline-flex min-h-6 items-center rounded-full bg-n-iris-9/15 px-1.5 font-mono text-[11px] font-semibold tabular-nums leading-none text-n-iris-11"
        >
          {{ node.value }}
        </span>
        <span
          v-else-if="node.type === 'ui'"
          class="font-semibold text-n-slate-12"
        >
          {{ node.value }}
        </span>
        <span v-else>{{ node.value }}</span>
      </template>
      <span v-if="hasSpot" class="sr-only">
        {{ t('ONBOARDING.MANUAL.SPOT_ITEM') }}
      </span>
    </p>
    <span
      v-if="hasSpot"
      class="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full text-n-iris-11 opacity-70 group-hover:bg-n-iris-9/15 group-hover:opacity-100 group-focus:bg-n-iris-9/15 group-focus:opacity-100"
      aria-hidden="true"
    >
      <span class="i-ri-focus-3-line size-3.5" />
    </span>
  </li>
</template>
